import { useMemo } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { DoseMatrix } from '~/domain/patient/doseMatrix';
import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import type { StructureSet } from '~/domain/patient/structureset';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { toSeriesInstanceUid } from '~/domain/dicom/uids';
import {
    gridImageRowsRequestState,
    gridRowImagesState,
    ReferenceComparison,
    referenceComparisonState,
} from '~/state/atoms/viewportGridAtoms';

export interface ReferenceShown {
    readonly structureSet?: StructureSet;
    readonly dose?: DoseMatrix;
}

export interface ReferenceRow {
    readonly comparison: ReferenceComparison | null;
    show(
        image: Image3D,
        relating: RigidRegistration | undefined,
        shown: ReferenceShown
    ): boolean;
    end(image: Image3D): void;
}

// A row of another image, with its own set and dose read-only in its own frame; the user's other rows stay.
export function useReferenceRow(): ReferenceRow {
    const comparison = useRecoilValue(referenceComparisonState);

    const show = useRecoilCallback(
        ({ snapshot, set }) =>
            (
                image: Image3D,
                relating: RigidRegistration | undefined,
                shown: ReferenceShown
            ): boolean => {
                if (
                    !getDatasetEnvironmentRegistry().setReference(
                        image.seriesUid,
                        shown
                    )
                ) {
                    return false;
                }
                const rows = snapshot
                    .getLoadable(gridRowImagesState)
                    .getValue();
                const seriesUid = toSeriesInstanceUid(image.seriesUid);
                const previous = snapshot
                    .getLoadable(referenceComparisonState)
                    .getValue();
                const request = snapshot
                    .getLoadable(gridImageRowsRequestState)
                    .getValue();
                // A row the latest request closes is going, even while the grid still shows it.
                const present =
                    rows.includes(seriesUid) &&
                    (!request ||
                        request.seriesInstanceUids.includes(seriesUid));
                // Shown again for the same image, the row it opened is still its own.
                const openedRow =
                    previous?.seriesUid === image.seriesUid
                        ? previous.openedRow
                        : !present;
                set(referenceComparisonState, {
                    seriesUid: image.seriesUid,
                    registrationSopUid: relating?.sopInstanceUid,
                    openedRow,
                });
                if (!present) {
                    set(gridImageRowsRequestState, {
                        seriesInstanceUids: rows.includes(seriesUid)
                            ? rows
                            : [...rows, seriesUid],
                    });
                }
                return true;
            },
        []
    );

    const end = useRecoilCallback(
        ({ snapshot, set }) =>
            (image: Image3D) => {
                const current = snapshot
                    .getLoadable(referenceComparisonState)
                    .getValue();
                if (current?.seriesUid !== image.seriesUid) {
                    return;
                }
                getDatasetEnvironmentRegistry().setReference(undefined);
                set(referenceComparisonState, null);
                if (!current.openedRow) {
                    return;
                }
                const rows = snapshot
                    .getLoadable(gridRowImagesState)
                    .getValue();
                set(gridImageRowsRequestState, {
                    seriesInstanceUids: rows.filter(
                        (uid) => uid !== image.seriesUid
                    ),
                });
            },
        []
    );

    return useMemo(() => ({ comparison, show, end }), [comparison, show, end]);
}
